import { useSyncExternalStore } from 'react';

/**
 * Which shape the Email tab is drawn in: the CRM's three panes, or a mail
 * client's list-then-reading-view.
 *
 * A preference about how somebody reads, not about the mailbox — two agents on
 * the same shared inbox can want different answers, and neither answer changes
 * a single row of data. It is therefore kept in the BROWSER rather than on the
 * server: nothing here is worth a migration, an endpoint and a `/meta` field,
 * and the cost is honest and small — sign in on a second machine and it opens
 * in the default until you say otherwise there too.
 *
 * Keyed by the same scope the composer drafts use (tenant + the agent's own
 * address), because a dispatch office shares browser profiles: the next person
 * to sign in at that desk must not inherit the layout the last one chose.
 */

export type MailLayout = 'crm' | 'gmail';

/** The CRM's own three panes. Nobody is moved by an upgrade. */
export const DEFAULT_MAIL_LAYOUT: MailLayout = 'crm';

const PREFIX = 'crm.mailLayout.';

export const isMailLayout = (value: unknown): value is MailLayout =>
  value === 'crm' || value === 'gmail';

/** What this browser holds for that agent, or the default. */
export function readMailLayout(scope: string): MailLayout {
  try {
    const stored = localStorage.getItem(PREFIX + scope);
    return isMailLayout(stored) ? stored : DEFAULT_MAIL_LAYOUT;
  } catch {
    // A browser refusing storage still gets a working inbox, in the default.
    return DEFAULT_MAIL_LAYOUT;
  }
}

const listeners = new Set<() => void>();

export function writeMailLayout(scope: string, layout: MailLayout): void {
  try {
    localStorage.setItem(PREFIX + scope, layout);
  } catch {
    // Storage refused. Nothing else to do — the read below will keep answering
    // with the default, which is the honest thing for a browser that forgets.
  }
  for (const listener of listeners) listener();
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * The layout this agent reads mail in, live across every component.
 *
 * The snapshot re-reads storage rather than caching a copy: the toggle sits in
 * the rail and the layout is read by the view beside it, so a second copy is
 * exactly the thing that would fall out of step. Safe for
 * `useSyncExternalStore` because the value is a primitive — it compares equal
 * until somebody actually writes a different one.
 */
export function useMailLayout(scope: string): MailLayout {
  return useSyncExternalStore(
    subscribe,
    () => readMailLayout(scope),
    () => DEFAULT_MAIL_LAYOUT,
  );
}
